import React, { PureComponent } from "react"
import classNames from "classnames"
import { FormElement } from "./form"
import { withFieldLabelAndHint } from "./field"
import DefaultButton from "../Buttons/DefaultButton"
import { MinusIcon, PlusIcon } from "../Icon"

const AUTO_CHANGE_TIMEOUT_IN_MS = 750
const AUTO_CHANGE_INTERVAL_IN_MS = 150

const enforceInRange = (
  value: number,
  min: number | undefined,
  max: number | undefined
) => {
  let enforcedValue = value
  if (typeof max !== "undefined") {
    enforcedValue = Math.min(enforcedValue, max)
  }
  if (typeof min !== "undefined") {
    enforcedValue = Math.max(enforcedValue, min)
  }
  return enforcedValue
}

type Props = {
  value: number
  name?: string
  disabled?: boolean
  min?: number
  max?: number
  step: number
  onChange?: (value: number) => void
  children?: (value: number) => React.ReactNode
} & FormElement

type State = {
  value: number
}

class Stepper extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props)

    if (props.step < 0)
      throw new Error("Incorrect step value. Can't be below zero")

    this.onDecrement = this.onDecrement.bind(this)
    this.onIncrement = this.onIncrement.bind(this)
    this.changeValue = this.changeValue.bind(this)
    this.onPointerDown = this.onPointerDown.bind(this)
    this.onPointerUp = this.onPointerUp.bind(this)

    this.state = {
      value: enforceInRange(props.value, props.min, props.max),
    }
  }

  timeout: ReturnType<typeof setTimeout> | null = null
  interval: ReturnType<typeof setInterval> | null = null

  componentDidUpdate(_prevProps: Props, prevState: State) {
    if (prevState.value !== this.state.value) {
      this.changeValue(this.state.value)
    } else if (this.props.value !== this.state.value) {
      this.changeValue(this.props.value)
    }
  }

  onDecrement = () => {
    ;(this.props.min !== null || this.state.value > this.props.min) &&
      this.changeValue(
        parseFloat((this.state.value - this.props.step).toFixed(10))
      )
  }

  onIncrement = () => {
    ;(this.props.max !== null || this.state.value < this.props.max) &&
      this.changeValue(
        parseFloat((this.state.value + this.props.step).toFixed(10))
      )
  }

  onPointerDown = (callback: () => void) => {
    callback()
    this.timeout && clearTimeout(this.timeout)
    this.timeout = setTimeout(() => {
      this.interval && clearInterval(this.interval)
      this.interval = setInterval(callback, AUTO_CHANGE_INTERVAL_IN_MS)
    }, AUTO_CHANGE_TIMEOUT_IN_MS)
  }

  onPointerUp = () => {
    if (this.interval) {
      clearInterval(this.interval)
      this.interval = null
    }
    this.timeout && clearTimeout(this.timeout)
  }

  changeValue = (value: number) => {
    this.setState(
      {
        value: enforceInRange(value, this.props.min, this.props.max),
      },
      () => {
        this.props.onChange && this.props.onChange(this.state.value)
      }
    )
  }

  render() {
    const { name, status, disabled, min, max, children } = this.props

    return (
      <div
        className={classNames("cobalt-Stepper", {
          "cobalt-Stepper--disabled": disabled,
          "cobalt-Stepper--success": status === "success",
          "cobalt-Stepper--error": status === "error",
        })}
      >
        <div className="cobalt-Stepper__Wrapper">
          <DefaultButton
            className="cobalt-Stepper__ActionButton"
            disabled={disabled || this.state.value === min}
            onPointerDown={() => this.onPointerDown(this.onDecrement)}
            onPointerUp={this.onPointerUp}
            onPointerLeave={this.onPointerUp}
            type={"button"}
            data-label="stepper-minus"
          >
            <MinusIcon />
          </DefaultButton>
          <div className="cobalt-Stepper__ContentWrapper">
            {children ? children(this.state.value) : this.state.value}
            <input type="hidden" name={name} value={this.state.value} />
          </div>
          <DefaultButton
            className="cobalt-Stepper__ActionButton"
            disabled={disabled || this.state.value === max}
            onPointerDown={() => this.onPointerDown(this.onIncrement)}
            onPointerUp={this.onPointerUp}
            onPointerLeave={this.onPointerUp}
            type={"button"}
            data-label="stepper-plus"
          >
            <PlusIcon />
          </DefaultButton>
        </div>
      </div>
    )
  }
}

const wrappedComponent = withFieldLabelAndHint(Stepper)
wrappedComponent.displayName = "Stepper"

export { wrappedComponent as Stepper }

type StepperMetaProps = {
  children: React.ReactNode
}
const StepperMeta = ({ children }: StepperMetaProps) => {
  return <div className="cobalt-Stepper__Meta">{children}</div>
}

export { StepperMeta }
